import { Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SelectGroup } from 'components/Form/SelectGroup/SelectGroup';
import { SelectWrapper } from 'components/Form/SelectGroup/SelectWrapper';
import moment from 'moment';
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import DateSelect from '../../../components/Form/DateSelect/DateSelect';
import { GenericCheckbox } from '../../../components/Form/GenericCheckbox/GenericCheckbox';
import { TermSelect } from '../../../components/Form/TermSelect/TermSelectPrice';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { KPP_TERMS } from '../../../lib/constants';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import { getDateComponent } from '../../../lib/utils';
import { colors } from '../../../theme/theme';
import {
	AppState,
	ContractPricesState,
	ContractState,
	SalesType,
} from '../../../types/redux';
import { formatDateEn2De } from '../../../utils/formatString';

const useStyles = makeStyles((theme: Theme) => ({
	sectionTitle1: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	sectionTitle2: {
		marginBottom: theme.spacing(2),
	},
	checkboxSection: {
		marginTop: theme.spacing(3),
		display: 'flex',
	},
	checkboxText: {
		maxWidth: 440,
		marginLeft: theme.spacing(3),
		marginTop: 2,
		'&.with_errors': {
			color: colors.red,
		},
	},
	checkboxErrorMessage: {
		color: colors.red,
		fontSize: 14,
		borderTop: `1px solid ${colors.red}`,
		marginTop: theme.spacing(1),
		padding: theme.spacing(1, 1, 1, 0),
		display: 'inline-block',
	},
	errorMessage: {
		color: colors.red,
		fontSize: 14,
		marginLeft: 10,
		marginTop: theme.spacing(1),
	},
}));

interface Props extends StateProps, DispatchProps {
	onSubmit(data: any): void;
}

interface DateParts {
	day?: string;
	month?: string;
	year?: string;
}

const InternalKppTerm: FunctionComponent<Props> = (props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { trackEvent } = useUniversalTracking();

	const [selectedValue, setSelectedValue] = useState(props.contract.laufzeit);

	const initialBeginDate =
		props.contract.beginndatum || moment().format('YYYY-MM-DD');

	const calcEarliestRegistrationDate = (beginD: string): string =>
		moment(beginD)
			.subtract(10, 'years')
			.subtract(1, 'days')
			.format('YYYY-MM-DD');

	const [beginYear, setBeginYear] = useState(
		getDateComponent(initialBeginDate, 'year')
	);

	const [beginMonth, setBeginMonth] = useState(
		getDateComponent(initialBeginDate, 'month')
	);

	const [beginDay, setBeginDay] = useState(
		getDateComponent(initialBeginDate, 'day')
	);

	const [earliestRegistrationDate, setEarliestRegistrationDate] = useState(
		calcEarliestRegistrationDate(
			`${getDateComponent(initialBeginDate, 'year')}-${getDateComponent(
				initialBeginDate,
				'month'
			)}-${getDateComponent(initialBeginDate, 'day')}`
		)
	);

	const [isNewCar] = React.useState(props.contract.neufahrzeug);
	const [buyDateString] = React.useState(props.contract.kaufdatum);

	const [isCheckedNotOlderThan10Years, setIsCheckedNotOlderThan10Years] = React.useState(props.contract.neufahrzeug || false);
	const [isCheckedError, setIsCheckedError] = useState('');
	const [beginDateError, setBeginDateError] = useState('');

	const validateAndSubmit = () => {
		// Force validations
		const hasNoErrors = validateCheckbox(true) && validateBeginDate();

		if (hasNoErrors) {
			onSubmit();
		}
	};

	const onSubmit = () => {
		trackEvent({
			category: 'laufzeit-submit',
			action: 'click-event',
			label: 'laufzeit',
		});
		props.onSubmit({
			laufzeit: selectedValue,
			beginndatum: `${beginYear}-${beginMonth}-${beginDay}`,
			earliestRegistrationDate,
		});
	};

	const handleClickCheckbox = (value: boolean) => {
		setIsCheckedNotOlderThan10Years(value);
		trackEvent({
			category: 'fahrzeug-check-age',
			action: 'click-event',
			label: 'fahrzeug',
			value: value ? 1 : 0,
		});
	};

	const validateCheckbox = (forceValidation: boolean = false): boolean => {
		// Note: valueError === undefined will stop the validation from happening, we will start
		// validating as soon as length > 0 or an external function sets valueError !== undefined
		if (isCheckedNotOlderThan10Years) {
			setIsCheckedError('');

			return true;
		}

		if (forceValidation || isCheckedError !== '') {
			setIsCheckedError(t('kppTerm.errors.checkboxNotChecked'));

			return false;
		}

		return false;
	};

	const validateBeginDate = (date: DateParts = {}): boolean => {
		const nowDate = moment();
		const buyDate = moment(buyDateString);
		const beginD = moment(
			`${date.year || beginYear}-${date.month || beginMonth}-${
				date.day || beginDay
			}`
		);
		const earliestRegDate = calcEarliestRegistrationDate(
			beginD.format('YYYY-MM-DD')
		);

		let error = '';
		if (!beginD.isValid()) {
			// invalid date
			error = t('kppTerm.errors.invalidDate');
		} else if (nowDate.diff(beginD, 'days') > 0) {
			// can't begin insurance in the past
			error = t('kppTerm.errors.beginDatePast');
		} else if (buyDate.diff(beginD, 'days') > 0) {
			// can't begin insurance before buy date
			error = t('kppTerm.errors.beginDateBeforeBuyDate');
		} else if (
			isNewCar &&
			beginD.clone().subtract(1, 'years').diff(buyDate, 'days') > 0
		) {
			// can't begin insurance 12 month after buy date of new car
			error = t('kppTerm.errors.beginDateFutureNewCar');
		} else if (
			!isNewCar &&
			beginD.clone().subtract(6, 'months').diff(buyDate, 'days') > 0
		) {
			// can't begin insurance 6 month after buy date of used car
			error = t('kppTerm.errors.beginDateFutureUsedCar');
		}

		setBeginDateError(error);
		setEarliestRegistrationDate(earliestRegDate);

		return error === '';
	};

	return (
		<div className="kpp_term_main">
			<PageTitle title={t('kppTerm.title')} />
			<Typography
				variant="h3"
				className={`${classes.sectionTitle1} kppTermSelectTitle`}
			>
				{t('kppTerm.section1_title')}
			</Typography>
			<TermSelect
				termValues={KPP_TERMS}
				termPrices={props.contractPrices}
				isMonthly={props.contract.salesType === SalesType.KPP}
				recommendedValue={36}
				selectedValue={selectedValue}
				setCheckedValue={setSelectedValue}
			/>
			<Typography
				variant="h3"
				className={`${classes.sectionTitle2} kppBeginDateTitle`}
			>
				{t('kppTerm.section2_title')}
			</Typography>
			<SelectGroup>
				<SelectWrapper id="beginDay">
					<DateSelect
						selectedValue={beginDay}
						setSelectedValue={(value) => {
							setBeginDay(value);
							validateBeginDate({ day: value });
						}}
						placeholder={t('common.date.day')}
						error=""
						dataCy="start_day"
						dateType="day"
						defaultDate={moment().toDate()}
					/>
				</SelectWrapper>
				<SelectWrapper id="beginMonth">
					<DateSelect
						selectedValue={beginMonth}
						setSelectedValue={(value) => {
							setBeginMonth(value);
							validateBeginDate({ month: value });
						}}
						placeholder={t('common.date.month')}
						error=""
						dataCy="start_month"
						dateType="month"
						defaultDate={moment().toDate()}
					/>
				</SelectWrapper>
				<SelectWrapper id="beginYear">
					<DateSelect
						selectedValue={beginYear}
						setSelectedValue={(value) => {
							setBeginYear(value);
							validateBeginDate({ year: value });
						}}
						placeholder={t('common.date.year')}
						error=""
						dataCy="start_year"
						dateType="year"
						defaultDate={moment().add(1, 'years').toDate()}
					/>
				</SelectWrapper>
			</SelectGroup>
			{!!beginDateError && (
				<div className={classes.errorMessage}>{beginDateError}</div>
			)}
			{!isNewCar && (
				<div className={classes.checkboxSection}>
					<GenericCheckbox
						dataCy="not_old_car_check"
						checked={isCheckedNotOlderThan10Years}
						setChecked={handleClickCheckbox}
					/>
					<div
						className={`${classes.checkboxText} ${
							isCheckedError ? 'with_errors' : ''
						}`}
					>
						<Typography>
							{t('kppTerm.firstRegistrationAfter').replace(
								'%s',
								formatDateEn2De(earliestRegistrationDate)
							)}
						</Typography>
						{isCheckedError && (
							<div className={classes.checkboxErrorMessage}>
								{isCheckedError}
							</div>
						)}
					</div>
				</div>
			)}
			<NavigationBar onClickContinue={validateAndSubmit} />
		</div>
	);
};

interface StateProps {
	contract: ContractState;
	contractPrices: ContractPricesState;
}

interface DispatchProps {}

const mapStateToProps = (state: AppState) => ({
	contract: state.contract,
	contractPrices: state.contractPrices,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({});

export const KppTerm = connect(
	mapStateToProps,
	mapDispatchToProps
)(InternalKppTerm);
